import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useQueryClient, useIsFetching } from "react-query";
import { useDashboard } from "../DashboardPage/useDashboard";
import { useDecisions } from "./useDecisions";
import styles from "./DecisionsPage.module.css";
import moment from "moment";

import ProjectsPageCollapsible from "../ProjectsPage/ProjectsPageCollapsible";
import { ChevronLeft } from "react-ikonate";

import Select from "../Shared/Select";
import { Header } from "../Shared/Header/Header";
import { TabBar } from "../Shared/TabBar/TabBar";
import AddItemButton from "../Shared/AddItemButton";
import decisionsIcon from "../assets/Menu_Icon_Decisions.svg";
import ObjectSelect from "../Shared/ObjectSelect";

import DecisionStatsItem from "./DecisionStatsItem/";
import DecisionStatsMultipleChoiceItem from "./DecisionStatsMultipleChoiceItem/";
import Auth from "shared/@auth";
import { getNameOfTwoDaysMeeting } from "ManagementTeam/utils/meetingDateParse";
import { useAgenda } from "ManagementTeam/AgendaPage/useAgenda";
import ReactModal from "react-modal";
import { ReactComponent as DeleteIcon } from "../assets/Delete_Icon.svg";
import Button from "shared/Button";
import AddNewModal from "ManagementTeam/Shared/AddNewModal";
import MoveModal from "ManagementTeam/Shared/MoveModal";
import { nanoid } from "nanoid";
import { toTitleCase } from "shared/@utils/formatString";
import { useDocuments } from "ManagementTeam/DocumentsPage/useDocuments";
import { ReactComponent as SearchIcon } from "../assets/Magnifying_Glass.svg";
import Input from "shared/@forms/Input";
import Loader from "shared/Loader";

/**
 * View component that displays the Projects Page
 */

export function DecisionsPage({ meetingType, isHideInfo, setToggleInfo }) {
  const { hasPermission, account } = Auth.useContainer();
  const [refetchInterval, setRefetchInterval] = useState(1000 * 10);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();
  const isDecisionsFetching = useIsFetching(["decisions"]) > 0;
  const _ = require("lodash");
  const { downloadFile } = useDocuments();
  const {
    data: dashboardData,
    status: statusCurrentDecision,
    refetchDashboard,
    isFetching: isDashboardFetching,
  } = useDashboard(refetchInterval, true, true); // activate dashboard query explicitly
  const {
    agendaPost,
    editAgendaPut,
    deleteAgenda,
    moveAgendaPut,
    duplicateAgendaPost,
    agendaItemPublishPut,
    agendaItemPdfData,
  } = useAgenda();

  const [showAddNewModal, setShowAddNewModal] = useState(false);

  const [dashboard, setDashboard] = useState([]);

  const [deleteModelOpen, setDeleteModelOpen] = useState(false);
  const [deleteAgendaId, setDeleteAgendaId] = useState("");
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [movingItem, setMovingItem] = useState({});
  const [currentMeeting, setCurrentMeeting] = useState(null);

  const [editingAgendaId, setEditingAgendaId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState({});
  const [itemOwners, setItemOwners] = useState([]);
  const [isPastDecisionOpen, setIsPastDecisionOpen] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchDashboard(),
        queryClient.invalidateQueries(["decisions"]),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (showMoveModal || showAddNewModal || showEditModal) {
      setRefetchInterval(false);
    } else {
      setRefetchInterval(1000 * 10);
    }
  }, [showMoveModal, showAddNewModal, showEditModal]);
  useEffect(() => {
    setDashboard(
      dashboardData && dashboardData.meetings ? dashboardData.meetings : []
    );
  }, [dashboardData]);
  const { displayResultsPut, votingStatusPut, pastDecisions, votingProxyPut } =
    useDecisions();

  const months = {
    January: "01",
    February: "02",
    March: "03",
    April: "04",
    May: "05",
    June: "06",
    July: "07",
    August: "08",
    September: "09",
    October: "10",
    November: "11",
    December: "12",
  };

  // TODO:
  //  - need to convert selected option back to date format
  //  - compare converted option to startOn dates to determine which decisions show
  //  - reorganize dashboard data

  const [activeTitle, setActiveTitle] = useState("Current Decisions");

  const [year, setYear] = useState("");
  const [meetings, setMeetings] = useState(null);
  const [currentMeetingDate, setCurrentMeetingDate] = useState(null);
  const [currentMeetingId, setCurrentMeetingId] = useState(null);

  const [currentOptionList, setCurrentOptionList] = useState([]);

  // data being passed in for mapped component
  const [currentDecisionsData, setCurrentDecisionsData] = useState(null);

  const [pastSelectedOption, setPastSelectedOption] = useState("");
  const [pastOptionList, setPastOptionList] = useState([]);
  const [sortedPastDecisions, setSortedPastDecisions] = useState();
  const [currentSelectMonthYear, setCurrentSelectMonthYear] = useState("");
  useEffect(() => {
    const sortedDecisions = [];
    const years = [];

    for (let index = 0; index < pastDecisions.length; index++) {
      const meeting = pastDecisions[index];

      if (!meeting.agenda || meeting.agenda.length === 0) {
        continue;
      }

      const currentYear = meeting.dates[0].startsOn.substring(0, 4);

      if (!years.includes(currentYear)) {
        years.push(currentYear);
      }

      const monthYearDate = `${Object.keys(months).find((name) => {
        return months[name] === meeting.dates[0].startsOn.substring(5, 7);
      })} ${currentYear}`;

      const monthYearIndex = sortedDecisions.findIndex(
        (d) => d.monthYear === monthYearDate
      );

      if (monthYearIndex > -1) {
        for (let i = 0; i < meeting.agenda.length; i++) {
          sortedDecisions[monthYearIndex].decisionItems.push({
            ...meeting.agenda[i],
            dates: meeting.dates,
            users: meeting.users,
          });
        }
      } else {
        sortedDecisions.push({
          monthYear: monthYearDate,
          decisionItems: meeting.agenda.map((a) => ({
            ...a,
            dates: meeting.dates,
            users: meeting.users,
          })),
          year: currentYear,
        });
      }
    }

    const sortedYears = [...years].sort((a, b) => Number(b) - Number(a));

    setPastOptionList(sortedYears);
    setPastSelectedOption((prev) => prev || sortedYears[0] || "");
    setSortedPastDecisions(sortedDecisions);
  }, [pastDecisions]);
  // set default meeting
  useEffect(() => {
    if (meetings && meetings.length > 0) {
      if (
        !currentMeetingId ||
        !meetings.find((a) => a.id == currentMeetingId)
      ) {
        setCurrentMeetingId(meetings[0]._id);
        setCurrentMeeting(meetings[0]);
      }
    }
  }, [meetings, currentMeetingId]);

  // set initial select option
  useEffect(() => {
    if (meetings && currentOptionList.length > 0) {
      if (
        !currentMeetingId ||
        !currentOptionList.find((a) => a.id == currentMeetingId)
      ) {
        setCurrentMeetingDate(currentOptionList[0]);
      }
    }
  }, [meetings, currentOptionList, currentMeetingId]);

  useMemo(() => {
    if (dashboard !== undefined)
      setMeetings(
        dashboard.sort(function compare(a, b) {
          var dateA = new Date(a.dates[0].startsOn);
          var dateB = new Date(b.dates[0].startsOn);
          return dateA - dateB;
        })
      );
  }, [dashboard]);

  useMemo(() => {
    if (!itemOwners?.length) {
      if (meetings?.length) {
        const memberNames = meetings[0].users
          .filter((member) => member.accessLevel.includes("MT1"))
          .map((member) => ({
            id: member._id,
            option: `${
              member.profile.firstName.toLowerCase().charAt(0).toUpperCase() +
              member.profile.firstName.toLowerCase().slice(1)
            } ${
              member.profile.lastName.toLowerCase().charAt(0).toUpperCase() +
              member.profile.lastName.toLowerCase().slice(1)
            }`,
            photoUrl: member.profile.photoUrl,
            accessLevel: member.accessLevel,
            lastName:
              member.profile.lastName.toLowerCase().charAt(0).toUpperCase() +
              member.profile.lastName.toLowerCase().slice(1),
            firstName:
              member.profile.firstName.toLowerCase().charAt(0).toUpperCase() +
              member.profile.firstName.toLowerCase().slice(1),
          }));

        const attorneysIBO = meetings[0].users
          .filter((member) => member.profile.firstName === "Attorneys")
          .map((member) => ({
            id: member._id,
            option: `${member.profile.firstName} ${member.profile.lastName}`,
            photoUrl: member.profile.photoUrl,
            accessLevel: member.accessLevel,
            lastName: member.profile.lastName,
            firstName: member.profile.firstName,
          }));

        const wwgIBO = meetings[0].users
          .filter(
            (member) =>
              member.profile.firstName === "WWG" ||
              member.profile.firstName === "WWG Guest"
          )
          .map((member) => ({
            id: member._id,
            option: `${member.profile.firstName} ${member.profile.lastName}`,
            photoUrl: member.profile.photoUrl,
            accessLevel: member.accessLevel,
            lastName: member.profile.lastName,
            firstName: member.profile.firstName,
          }));

        const tsika = meetings[0].users
          .filter(
            (member) =>
              member.profile.firstName.toUpperCase() === "PAUL" &&
              member.profile.lastName.toUpperCase() === "TSIKA"
          )
          .map((member) => ({
            id: member._id,
            option: toTitleCase(
              `${member.profile.firstName} ${member.profile.lastName}`
            ),
            photoUrl: member.profile.photoUrl,
            accessLevel: member.accessLevel,
            lastName: member.profile.lastName,
            firstName: member.profile.firstName,
          }));

        setItemOwners(
          [attorneysIBO[0], ...wwgIBO, tsika[0], ...memberNames].sort(
            (a, b) => {
              const result = a.lastName.localeCompare(b.lastName);

              return result !== 0
                ? result
                : a.firstName.localeCompare(b.firstName);
            }
          )
        );
      }
    }
  }, [meetings, itemOwners]);

  // generates item positions based on index for mc items
  var alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  var result = "";
  function printToLetter(number) {
    result = "";
    var charIndex = (number + 1) % alphabet.length;
    var quotient = (number + 1) / alphabet.length;
    if (charIndex - 1 === -1) {
      charIndex = alphabet.length;
      quotient--;
    }
    result = alphabet.charAt(charIndex - 1) + result;
    if (quotient >= 1) {
      printToLetter(parseInt(quotient));
    } else {
      return result;
    }
  }
  const checkChecked = (array, i) => {
    return array.attendees?.some((e) => (e._id === i._id ? true : false));
  };
  const checkCheckedProxy = (array, i) => {
    return array.attendees?.some((e) =>
      e._id === i._id && e.proxyCheckInBy ? true : false
    );
  };
  // data passed into decision item
  // request a multiple choice example for testing
  useEffect(() => {
    if (meetings !== null) {
      setCurrentDecisionsData([]);
      meetings.map((i, index) => {
        const indexEnded = meetings[index]?.dates
          ? meetings[index].dates.findIndex((date) => date.sessionEnded) + 1
          : 0;

        if (i.agenda.length > 0) {
          i.agenda.map((decision) => {
            if (decision.decision._id) {
              var diamonds = [];
              var multipleChoiceOptions = [];
              var multipleChoiceResults = [];

              meetings[index].users.map((diamond) => {
                var selections = [];
                if (
                  // diamond.accessLevel.includes("EC") ||
                  diamond.accessLevel.includes("MT1")
                ) {
                  // get array of decisions for individual diamond
                  decision.decision.choices.map((choice) => {
                    if (choice.votes.some((i) => i._id === diamond._id)) {
                      selections.push(choice.description);
                    }
                    return null;
                  });
                  diamonds.push({
                    id: diamond._id,
                    firstName: diamond.profile.firstName,
                    lastName: diamond.profile.lastName,
                    photo: diamond.profile.photoUrl,
                    // checkedIn:
                    //   meetings[index] &&
                    //   meetings[index].dates &&
                    //   meetings[index].dates.length > 0 &&
                    //   meetings[index].dates[0].attendees
                    //     ? meetings[index].dates[0].attendees.some((e) =>
                    //         e._id === diamond._id ? true : false
                    //       )
                    //     : null,
                    checkedIn: meetings[index]
                      ? checkChecked(meetings[index].dates[indexEnded], diamond)
                      : false,
                    proxyCheckedIn: meetings[index]
                      ? checkCheckedProxy(
                          meetings[index].dates[indexEnded],
                          diamond
                        )
                      : false,
                    votingStatus: decision.decision.choices.some(
                      (e) =>
                        e.description === "YES" &&
                        e.votes.some((i) => i._id === diamond._id)
                    )
                      ? "Yes"
                      : decision.decision.choices.some(
                          (e) =>
                            e.description === "NO" &&
                            e.votes.some((i) => i._id === diamond._id)
                        )
                      ? "No"
                      : "Pending",
                    isProxyVote: decision.decision.choices.some((e) =>
                      e.votes.some(
                        (i) => i._id === diamond._id && i.proxyVoteBy
                      )
                    ),
                    // role: diamond.accessLevel.includes("EC")
                    //   ? "EC"
                    //   : diamond.accessLevel.includes("MT1")
                    //   ? "MT1"
                    //   : "MT2",
                    role: diamond.accessLevel.includes("MT1") ? "MT1" : "MT2",
                    selections: selections,
                  });
                  return null;
                }
                return null;
              });
              // create list of mc options and results for mc decisions
              decision.decision.choices.map((choice, index) => {
                multipleChoiceResults.push({
                  optionName: choice.description,
                  votes: choice.votes.length,
                });
                multipleChoiceOptions.push({
                  itemTitle: choice.description,
                  itemPosition: printToLetter(index),
                });
                return null;
              });
              setCurrentDecisionsData((prev) => [
                ...prev,
                {
                  date: `${Object.keys(months).find((name) => {
                    if (months[name] === i.dates[0].startsOn.substring(5, 7)) {
                      return name;
                    }
                    return null;
                  })}${i.dates.map((date, index) => {
                    if (i.dates.length === 1) {
                      return ` ${date.startsOn.substring(8, 10)}`;
                    } else if (i.dates.length > 1) {
                      if (
                        index !== 0 &&
                        i.dates[index - 1].startsOn.substring(8, 10) >
                          i.dates[index].startsOn.substring(8, 10)
                      ) {
                        return ` ${Object.keys(months).find((name) => {
                          if (
                            months[name] ===
                            `${zeroPad(
                              parseInt(i.dates[0].startsOn.substring(5, 7)) + 1,
                              2
                            )}`
                          ) {
                            return name;
                          }
                          return null;
                        })} ${date.startsOn.substring(8, 10)}`;
                      } else {
                        return ` ${date.startsOn.substring(8, 10)}`;
                      }
                    }
                    return null;
                  })} ${i.dates[0].startsOn.substring(0, 4)}`,
                  meetingId: i._id,
                  agenda: decision,
                  assignee: decision.owner.name,
                  assignedType: decision.owner.accessLevel.includes("EC")
                    ? "WWG"
                    : "Diamond",
                  title: decision.title,
                  isDraft: decision.isDraft,
                  description:
                    decision.description === null ? "" : decision.description,
                  isCompleted: decision.isComplete,
                  votingStatus:
                    decision.decision.status === "on"
                      ? "Voting in Progress"
                      : decision.decision.status === "off"
                      ? "Upcoming"
                      : decision.decision.votingStatus,
                  document: decision.document,
                  pdfName: decision.document
                    ? decision.document.files[0].name
                    : "PDF",
                  displayResults:
                    decision.decision.displayResults !== undefined
                      ? decision.decision.displayResults
                      : false,
                  voting:
                    decision.decision.status === undefined ||
                    decision.decision.status.match(/C|closed/)
                      ? "closed"
                      : decision.decision.status.match(/O|off/)
                      ? "off"
                      : decision.decision.status.match(/(O|open)|(O|on)/)
                      ? "on"
                      : "closed",
                  totalVotes: diamonds.length,
                  yesVotesA:
                    decision.decision.choices.length < 2
                      ? 0
                      : decision.decision.choices[0].votes.filter(
                          ({ block }) => block === "A"
                        ).length,
                  noVotesA:
                    decision.decision.choices.length < 2
                      ? 0
                      : decision.decision.choices[1].votes.filter(
                          ({ block }) => block === "A"
                        ).length,
                  yesVotesB:
                    decision.decision.choices.length < 2
                      ? 0
                      : decision.decision.choices[0].votes.filter(
                          ({ block }) => block === "B"
                        ).length,
                  noVotesB:
                    decision.decision.choices.length < 2
                      ? 0
                      : decision.decision.choices[1].votes.filter(
                          ({ block }) => block === "B"
                        ).length,
                  choices: decision.decision.choices,
                  voteCount: decision.decision.voteCount,
                  attendanceCount: decision.decision.attendanceCount,
                  diamonds: diamonds,
                  isBlockA: decision.decision.isBlockA,
                  isBlockB: decision.decision.isBlockB,
                  multipleChoiceVoteMade:
                    decision.decision.choices.filter(
                      (c) => c.description !== "YES" && c.description !== "NO"
                    ).length > 0
                      ? decision.decision.choices.filter(
                          (c) =>
                            c.description !== "YES" && c.description !== "NO"
                        )[0].votes.length
                      : 0,
                  yesVotes:
                    decision.decision.choices.filter(
                      (c) => c.description === "YES"
                    ).length > 0
                      ? decision.decision.choices.filter(
                          (c) => c.description === "YES"
                        )[0].votes.length
                      : 0,
                  noVotes:
                    decision.decision.choices.filter(
                      (c) => c.description === "NO"
                    ).length > 0
                      ? decision.decision.choices.filter(
                          (c) => c.description === "NO"
                        )[0].votes.length
                      : 0,
                  isMultipleChoice: decision.decision.isMultipleChoice,
                  //TODO: this needs to take isMultipleChoice boolean property, but currently is set to false regardless of voting type
                  multipleChoiceResults: multipleChoiceResults,
                  multipleChoiceOptions: multipleChoiceOptions,
                  id:
                    decision.decision._id !== undefined
                      ? decision.decision._id
                      : null,
                },
              ]);
              return null;
            } else {
              return null;
            }
          });
        }
        return null;
      });
    } else return null;
  }, [meetings]);
  const zeroPad = (num, places) => String(num).padStart(places, "0");
  const formattedDates = useMemo(() => {
    if (meetings !== null) {
      return _.uniq(
        _.map(meetings, function (decision, index) {
          if (decision.agenda.length > 0)
            return `${Object.keys(months).find((name) => {
              if (months[name] === decision.dates[0].startsOn.substring(5, 7)) {
                return name;
              }
              return null;
            })}${decision.dates.map((date, index) => {
              if (decision.dates.length === 1) {
                return ` ${date.startsOn.substring(8, 10)}`;
              } else if (decision.dates.length > 1) {
                if (
                  index !== 0 &&
                  decision.dates[index - 1].startsOn.substring(8, 10) >
                    decision.dates[index].startsOn.substring(8, 10)
                ) {
                  return ` ${Object.keys(months).find((name) => {
                    if (
                      months[name] ===
                      `${zeroPad(
                        parseInt(decision.dates[0].startsOn.substring(5, 7)) +
                          1,
                        2
                      )}`
                    ) {
                      return name;
                    }
                    return null;
                  })} ${date.startsOn.substring(8, 10)}`;
                } else {
                  return ` ${date.startsOn.substring(8, 10)}`;
                }
              }
              return null;
            })} ${decision.dates[0].startsOn.substring(0, 4)}`;
        })
      );
    } else {
      return null;
    }
  }, [meetings]);

  // get meeting ids and dates
  useEffect(() => {
    var selectOptions = [];
    meetings &&
      meetings.length > 0 &&
      meetings.map((meeting) => {
        let dateString =
          meeting.dates.length > 0
            ? meeting.dates.map((date) =>
                moment.utc(date.startsOn).format("MMMM DD, YYYY")
              )
            : [];

        var meetingDatesArray =
          meeting.dates.length > 0
            ? meeting.dates.map((date) =>
                moment.utc(date.startsOn).format("MMMM DD, YYYY")
              )
            : [];

        return selectOptions.push({
          id: meeting._id,
          option:
            dateString.length === 2
              ? getNameOfTwoDaysMeeting(dateString)
              : dateString[0],
          dates: meetingDatesArray,
        });
      });
    setCurrentOptionList(selectOptions);
  }, [meetings]);

  const [currentMeetingData, setCurrentMeetingData] = useState(null);
  useEffect(() => {
    if (currentDecisionsData && currentDecisionsData.length > 0) {
      var _currentMeetingData = [];
      currentDecisionsData.map(
        (decision, index) =>
          decision.meetingId === currentMeetingId &&
          _currentMeetingData.push(decision)
      );
      setCurrentMeetingData(_currentMeetingData);
    }
  }, [currentDecisionsData, currentMeetingId]);

  const options = useMemo(() => {
    if (formattedDates !== null) {
      return formattedDates;
    } else {
      return null;
    }
  }, [formattedDates]);

  const initialValue = useMemo(() => {
    if (formattedDates !== null) {
      return formattedDates[0];
    } else {
      return null;
    }
  }, [formattedDates]);

  useMemo(() => {
    setCurrentOptionList(options);
  }, [initialValue]);

  const handlePastChange = (e) => {
    if (e !== "") {
      setYear(e);
      setPastSelectedOption(e);
    }
  };

  const handleCurrentChange = (e) => {
    var index = 0;
    currentOptionList.map((meeting, i) => {
      if (meeting.id === e) {
        index = i;
      }
      return null;
    });
    setCurrentMeetingDate(currentOptionList[index]);
    setCurrentMeetingId(meetings.find((meeting) => meeting._id === e)._id);
    setCurrentMeeting(meetings.find((meeting) => meeting._id === e));
  };

  const handleToggle = (e, id) => {
    displayResultsPut.mutate({
      decisionId: id,
      displayResults: e,
    });
  };
  const handleChangeVotingStatus = (e, id) => {
    votingStatusPut.mutate(
      {
        decisionId: id,
        status: e,
      },
      {
        onSuccess: () => {
          refetchDashboard();
        },
      }
    );
  };

  const readOnly = !hasPermission("management-team.admins");

  const handleDeleteAgenda = (selectedId) => {
    setCurrentDecisionsData(
      currentDecisionsData.filter((item) => item._id !== selectedId)
    );
    deleteAgenda.mutate({
      agendaId: selectedId,
    });
  };

  // POST New Agenda
  const handleSaveNew = (data) => {
    const agendaObject = {
      meetingId: currentMeetingId,
      documentId: data.documentId,
      isDocumentDownloadable: data.isDocumentDownloadable,
      filesToUpload: data.isUploadDocument ? data.documents : [],
      meetingType: meetingType,
      owner: {
        _id: data.itemOwner.id,
        name: data.itemOwner.option,
        photoUrl: data.itemOwner.photoUrl,
        accessLevel: data.itemOwner.accessLevel,
      },
      title: data.title,
      description: data.description,
      notes: data.notes.map((note) => ({
        note: note.content,
        postedBy: {
          name: note.author,
          _id: note.postedBy
            ? note.postedBy._id
            : currentMeeting.users.find(
                (user) => user.profile.email === note.username
              )._id,
        },
        location: note.location,
        submittedOn: note.date,
      })),
      decision: data.addTo.includes("Decisions (Voting)")
        ? {
            // requiresQuorum: data.votingRequirements.includes("Requires Quorum"),
            isBlockA: data.votingRequirements.includes('"A Block" Vote'),
            isBlockB: data.votingRequirements.includes('"B Block" Vote'),
            isMultipleChoice:
              data.votingRequirements.includes("Multiple Choice"),
            maximumChoices: data.allowMultiple ? data.maximumChoices : 1,
            choices: data.selections,
          }
        : null,
      isDraft: false,
      ifNeeded: data.ifNeeded,
    };

    agendaPost.mutate(agendaObject);
    agendaObject.isNew = true;
    // setCurrentAgendaItems((prev) => [...prev, agendaObject]);
    setShowAddNewModal(false);
    // setIsModalOpen(false);
    setTimeout(() => {
      refetchDashboard();
    }, 1000);
  };
  const handleSaveNewDraft = (data) => {
    const agendaObject = {
      meetingId: currentMeetingId,
      agendaId: editingAgendaId,
      documentId: data.documentId,
      isDocumentDownloadable: data.isDocumentDownloadable,
      filesToUpload: data.isUploadDocument ? data.documents : [],
      meetingType: meetingType,
      owner: {
        _id: data.itemOwner.id,
        name: data.itemOwner.option,
        photoUrl: data.itemOwner.photoUrl,
        accessLevel: data.itemOwner.accessLevel,
      },
      title: data.title,
      description: data.description,
      notes: data.notes.map((note) => ({
        note: note.content,
        postedBy: {
          name: note.author,
          _id: note.postedBy
            ? note.postedBy._id
            : currentMeeting.users.find(
                (user) => user.profile.email === note.username
              )._id,
        },
        location: note.location,
      })),
      decision: data.addTo.includes("Decisions (Voting)")
        ? {
            isBlockA: data.votingRequirements.includes('"A Block" Vote'),
            isBlockB: data.votingRequirements.includes('"B Block" Vote'),
            isMultipleChoice:
              data.votingRequirements.includes("Multiple Choice"),
            maximumChoices: data.allowMultiple ? data.maximumChoices : 1,
            choices: data.selections,
            votingStatus: "Upcoming",
            status: "off",
            displayResults: false,
          }
        : null,
      isDraft: true,
      ifNeeded: data.ifNeeded,
    };

    agendaPost.mutate(agendaObject);
    agendaObject.isNew = false;
    setShowAddNewModal(false);
    setShowEditModal(false);
    // setIsModalOpen(false);
    setTimeout(() => {
      refetchDashboard();
    }, 1000);
  };
  const handleSaveEdit = (data) => {
    const agendaObject = {
      meetingId: data.meetingId,
      agendaId: editingAgendaId,
      documentId: data.documentId,
      isDocumentDownloadable: data.isDocumentDownloadable,
      filesToUpload: data.isUploadDocument ? data.documents : [],
      meetingType: meetingType,
      owner: {
        _id: data.itemOwner.id,
        name: data.itemOwner.option,
        photoUrl: data.itemOwner.photoUrl,
        accessLevel: data.itemOwner.accessLevel,
      },
      title: data.title,
      description: data.description,
      notes:
        (data.notes &&
          data.notes.map((note) => {
            let postedBy = {
              name: note.author,
            };

            if (note.postedBy) {
              postedBy._id = note.postedBy._id;
            } else {
              let user =
                (currentMeeting.users &&
                  currentMeeting.users.find(
                    (user) => user.profile.email === note.username
                  )) ||
                null;
              if (user) {
                postedBy._id = user._id;
              }
            }
            return {
              note: note.content,
              postedBy: postedBy,
              submittedOn: note.date,
              location: note.location,
            };
          })) ||
        [],
      decision: data.addTo.includes("Decisions (Voting)")
        ? {
            isBlockA: data.votingRequirements.includes('"A Block" Vote'),
            isBlockB: data.votingRequirements.includes('"B Block" Vote'),
            isMultipleChoice:
              data.votingRequirements.includes("Multiple Choice"),
            maximumChoices: data.allowMultiple ? data.maximumChoices : 1,
            choices: data.selections,
          }
        : null,
      isDraft: false,
      ifNeeded: data.ifNeeded,
    };
    editAgendaPut.mutate(agendaObject);
    agendaObject.isNew = false;
    setShowEditModal(false);
    // setIsModalOpen(false);
    setTimeout(() => {
      refetchDashboard();
    }, 1000);
  };
  const handleEdit = (data) => {
    if (data.files && data.files.length > 0) {
      Promise.all(
        data.files.map(async (file) => {
          return {
            type: file.type,
            src: file.src,
            data: await agendaItemPdfData(
              file.src.match(/agendas\/.*?$/g)[0].replace("agendas/", "")
            ).then(async (res) => {
              const blob = await res.blob();
              const url = window.URL.createObjectURL(blob);
              const response = await fetch(url);
              const res_1 = response.text();
              return res_1;
            }),
          };
        })
      ).then((result) => {
        const notes = data.notes.map((note) => {
          return {
            content: note.note,
            author: note.postedBy.name,
            date: note.submittedOn,
            postedBy: note.postedBy,
            location: note.location,
          };
        });
        setEditingAgendaId(data._id);
        const choices = data.decision.choices.map(
          (choice) => choice.description
        );
        const itemInfo = {
          id: data._id,
          meetingId: data.meeting,
          isDraft: data.isDraft,
          isActionItem: data.isActionItem,
          itemOwner: data.owner,
          title: data.title,
          description: data.description,
          notes: [...notes],
          addTo: ["Agenda", choices.length > 0 ? "Decisions (Voting)" : null],
          votingRequirements: [],
          allowMultiple: data.decision.maximumChoices > 1,
          maximumChoices: data.decision.maximumChoices,
          selections: [...choices],
          documents: result,
        };
        if (data.decision.isBlockA) {
          itemInfo.votingRequirements.push('"A Block" Vote');
        }
        if (data.decision.isBlockB) {
          itemInfo.votingRequirements.push('"B Block" Vote');
        }
        if (data.decision.isMultipleChoice) {
          itemInfo.votingRequirements.push("Multiple Choice");
        }
        setEditingItem(itemInfo);
        setShowEditModal(true);
        // setIsModalOpen(true);
      });
    } else {
      const notes = data.notes?.map((note) => {
        return {
          content: note.note,
          author: note.postedBy.name,
          date: note.submittedOn,
          postedBy: note.postedBy,
          location: note.location,
        };
      });
      setEditingAgendaId(data._id);
      const choices = data.decision.choices.map((choice) => choice.description);
      const itemInfo = {
        id: data._id,
        meetingId: data.meeting,
        isActionItem: data.isActionItem,
        isDraft: data.isDraft,
        itemOwner: data.owner,
        title: data.title,
        description: data.description,
        notes: notes ? [...notes] : [],
        addTo: ["Agenda", choices.length > 0 ? "Decisions (Voting)" : null],
        votingRequirements: [],
        allowMultiple: data.decision.maximumChoices > 1,
        maximumChoices: data.decision.maximumChoices,
        selections: [...choices],
        document: data.document,
        documents: [],
      };
      if (data.decision.isBlockA) {
        itemInfo.votingRequirements.push('"A Block" Vote');
      }
      if (data.decision.isBlockB) {
        itemInfo.votingRequirements.push('"B Block" Vote');
      }
      if (data.decision.isMultipleChoice) {
        itemInfo.votingRequirements.push("Multiple Choice");
      }
      setEditingItem(itemInfo);
      setShowEditModal(true);
      // setIsModalOpen(true);
    }
  };

  const handleProxyVote = (e, agendaId) => {
    const proxyUsers = e.filter((user) => user.isProxyVote);
    const payload = {
      proxyUsers,
      agendaId,
    };
    votingProxyPut.mutateAsync(payload).then(() => {
      refetchDashboard();
    });
  };

  const handleMove = (destinatedMeeting) => {
    moveAgendaPut.mutate({
      agendaId: movingItem._id,
      addToMeetingId: destinatedMeeting.id,
    });
    setShowMoveModal(false);
    refetchDashboard();
  };
  const handleDuplicate = (destinatedMeeting) => {
    duplicateAgendaPost.mutate({
      agendaId: movingItem._id,
      addToMeetingId: destinatedMeeting.id,
    });
    setShowMoveModal(false);
    refetchDashboard();
  };
  //Logic for publishing the agenda PDF goes here
  const handlePublishAgenda = (data) => {
    agendaItemPublishPut.mutate({
      agendaId: data._id,
      published: true,
    });
  };
  const handleDownloadPdf = (e) => {
    e.map(async (file) => {
      await downloadFile(file.downloadPath);
    });
  };
  const handleRenderPastDecision = (list = null) => {
    let decisionsList = list;
    if (!decisionsList) {
      decisionsList = sortedPastDecisions.find(
        (d) => currentSelectMonthYear === d.monthYear
      );
    }
    if (!decisionsList) return;
    const decisionsListToRender = decisionsList.decisionItems.map(
      (decision) => {
        const multipleChoiceResults = [];
        const multipleChoiceOptions = [];
        const diamonds = [];
        const indexEnded = decision?.dates
          ? decision.dates.findIndex((date) => date.sessionEnded) + 1
          : 0;
        decision.users?.map((diamond) => {
          var selections = [];
          if (
            // diamond.accessLevel.includes("EC") ||
            diamond.accessLevel.includes("MT1")
          ) {
            // get array of decisions for individual diamond
            decision.decision.choices.map((choice) => {
              if (choice.votes.some((i) => i._id === diamond._id)) {
                selections.push(choice.description);
              }
              return null;
            });
            diamonds.push({
              id: diamond._id,
              firstName: diamond.profile.firstName,
              lastName: diamond.profile.lastName,
              photo: diamond.profile.photoUrl,
              // checkedIn:
              //   meetings[index] &&
              //   meetings[index].dates &&
              //   meetings[index].dates.length > 0 &&
              //   meetings[index].dates[0].attendees
              //     ? meetings[index].dates[0].attendees.some((e) =>
              //         e._id === diamond._id ? true : false
              //       )
              //     : null,
              checkedIn: decision
                ? checkChecked(decision.dates[indexEnded], diamond)
                : false,
              proxyCheckedIn: decision
                ? checkCheckedProxy(decision.dates[indexEnded], diamond)
                : false,
              votingStatus: decision.decision.choices.some(
                (e) =>
                  e.description === "YES" &&
                  e.votes.some((i) => i._id === diamond._id)
              )
                ? "Yes"
                : decision.decision.choices.some(
                    (e) =>
                      e.description === "NO" &&
                      e.votes.some((i) => i._id === diamond._id)
                  )
                ? "No"
                : "Pending",
              isProxyVote: decision.decision.choices.some((e) =>
                e.votes.some((i) => i._id === diamond._id && i.proxyVoteBy)
              ),
              // role: diamond.accessLevel.includes("EC")
              //   ? "EC"
              //   : diamond.accessLevel.includes("MT1")
              //   ? "MT1"
              //   : "MT2",
              role: diamond.accessLevel.includes("MT1") ? "MT1" : "MT2",
              selections: selections,
            });
            return null;
          }
          return null;
        });
        decision.decision.choices.map((choice, index) => {
          multipleChoiceResults.push({
            optionName: choice.description,
            votes: choice.votes.length,
          });
          multipleChoiceOptions.push({
            itemTitle: choice.description,
            itemPosition: printToLetter(index),
          });
          return null;
        });
        return {
          date: `${Object.keys(months).find((name) => {
            if (months[name] === decision.dates[0].startsOn.substring(5, 7)) {
              return name;
            }
            return null;
          })}${decision.dates.map((date, index) => {
            if (decision.dates.length === 1) {
              return ` ${date.startsOn.substring(8, 10)}`;
            } else if (decision.dates.length > 1) {
              if (
                index !== 0 &&
                decision.dates[index - 1].startsOn.substring(8, 10) >
                  decision.dates[index].startsOn.substring(8, 10)
              ) {
                return ` ${Object.keys(months).find((name) => {
                  if (
                    months[name] ===
                    `${zeroPad(
                      parseInt(decision.dates[0].startsOn.substring(5, 7)) + 1,
                      2
                    )}`
                  ) {
                    return name;
                  }
                  return null;
                })} ${date.startsOn.substring(8, 10)}`;
              } else {
                return ` ${date.startsOn.substring(8, 10)}`;
              }
            }
            return null;
          })} ${decision.dates[0].startsOn.substring(0, 4)}`,
          meetingId: decision._id,
          agenda: decision,
          assignee: decision.owner.name,
          assignedType: decision.owner.accessLevel.includes("EC")
            ? "WWG"
            : "Diamond",
          title: decision.title,
          isDraft: decision.isDraft,
          description:
            decision.description === null ? "" : decision.description,
          isCompleted: decision.isComplete,
          votingStatus:
            decision.decision.status === "on"
              ? "Voting in Progress"
              : decision.decision.status === "off"
              ? "Upcoming"
              : decision.decision.votingStatus,
          document: decision.document,
          pdfName: decision.document ? decision.document.files[0].name : "PDF",
          displayResults:
            decision.decision.displayResults !== undefined
              ? decision.decision.displayResults
              : false,
          voting:
            decision.decision.status === undefined ||
            decision.decision.status.match(/C|closed/)
              ? "closed"
              : decision.decision.status.match(/O|off/)
              ? "off"
              : decision.decision.status.match(/(O|open)|(O|on)/)
              ? "on"
              : "closed",
          totalVotes: 0, //diamonds.length,
          yesVotesA:
            decision.decision.choices.length < 2
              ? 0
              : decision.decision.choices[0].votes.filter(
                  ({ block }) => block === "A"
                ).length,
          noVotesA:
            decision.decision.choices.length < 2
              ? 0
              : decision.decision.choices[1].votes.filter(
                  ({ block }) => block === "A"
                ).length,
          yesVotesB:
            decision.decision.choices.length < 2
              ? 0
              : decision.decision.choices[0].votes.filter(
                  ({ block }) => block === "B"
                ).length,
          noVotesB:
            decision.decision.choices.length < 2
              ? 0
              : decision.decision.choices[1].votes.filter(
                  ({ block }) => block === "B"
                ).length,
          choices: decision.decision.choices,
          voteCount: decision.decision.voteCount,
          attendanceCount: decision.decision.attendanceCount,
          diamonds: diamonds,
          isBlockA: decision.decision.isBlockA,
          isBlockB: decision.decision.isBlockB,
          multipleChoiceVoteMade:
            decision.decision.choices.filter(
              (c) => c.description !== "YES" && c.description !== "NO"
            ).length > 0
              ? decision.decision.choices.filter(
                  (c) => c.description !== "YES" && c.description !== "NO"
                )[0].votes.length
              : 0,
          yesVotes:
            decision.decision.choices.filter((c) => c.description === "YES")
              .length > 0
              ? decision.decision.choices.filter(
                  (c) => c.description === "YES"
                )[0].votes.length
              : 0,
          noVotes:
            decision.decision.choices.filter((c) => c.description === "NO")
              .length > 0
              ? decision.decision.choices.filter(
                  (c) => c.description === "NO"
                )[0].votes.length
              : 0,
          isMultipleChoice: decision.decision.isMultipleChoice,
          //TODO: this needs to take isMultipleChoice boolean property, but currently is set to false regardless of voting type
          multipleChoiceResults: multipleChoiceResults,
          multipleChoiceOptions: multipleChoiceOptions,
          id:
            decision.decision._id !== undefined ? decision.decision._id : null,
        };
      }
    );
    const dateRender = [];
    return decisionsListToRender.map((decision, index) => {
      const isDateRendered = dateRender.find((d) => d === decision.date);
      if (!isDateRendered) dateRender.push(decision.date);
      return (
        <div className={styles.projectItems} key={`${decision._id}-${index}`}>
          {!isDateRendered && (
            <div className={styles.datePastHeader}>
              {decision.date} Decision
            </div>
          )}
          {!decision.isMultipleChoice ? (
            <DecisionStatsItem
              readOnly={true}
              assignee={decision.assignee}
              assignedType={decision.assignedType}
              title={decision.title}
              description={decision.description}
              isCompleted={decision.isComplete}
              votingStatus={decision.votingStatus}
              pdfName={decision.pdfName}
              document={decision.document}
              diamonds={decision.diamonds}
              onClickPdf={() =>
                handleDownloadPdf(
                  decision.document &&
                    decision.document.files &&
                    decision.document.files.length > 0
                    ? decision.document.files
                    : null
                )
              }
              onEdit={() => handleEdit(decision.agenda)}
              onDelete={() => {
                setDeleteAgendaId(decision.agenda._id);
                setDeleteModelOpen(true);
              }}
              onMove={() => {
                setMovingItem(decision.agenda);
                setShowMoveModal(true);
                // setIsModalOpen(true);
              }}
              onPublish={() => handlePublishAgenda(decision.agenda)}
              displayResults={decision.displayResults}
              voting={decision.voting}
              totalVotes={decision.totalVotes}
              isBlockA={decision.isBlockA}
              isBlockB={decision.isBlockB}
              yesVotes={decision.yesVotes}
              noVotes={decision.noVotes}
              decision={decision}
              onToggle={(e) => handleToggle(e, decision.id)}
              onChangeVotingStatus={(e) =>
                handleChangeVotingStatus(e, decision.id)
              }
              handleProxyVote={handleProxyVote}
              isDraft={decision.isDraft}
              agendaId={decision.agenda._id}
              isHideInfo={isHideInfo}
            />
          ) : (
            <DecisionStatsMultipleChoiceItem
              assignee={decision.assignee}
              assignedType={decision.assignedType}
              title={decision.title}
              description={decision.description}
              isCompleted={decision.isComplete}
              votingStatus={decision.votingStatus}
              pdfName={decision.pdfName}
              document={decision.document}
              onClickPdf={() =>
                handleDownloadPdf(
                  decision.document &&
                    decision.document.files &&
                    decision.document.files.length > 0
                    ? decision.document.files
                    : null
                )
              }
              onEdit={() => handleEdit(decision.agenda)}
              onDelete={() => {
                setDeleteAgendaId(decision.agenda._id);
                setDeleteModelOpen(true);
              }}
              onMove={() => {
                setMovingItem(decision.agenda);
                setShowMoveModal(true);
                // setIsModalOpen(true);
              }}
              onPublish={() => handlePublishAgenda(decision.agenda)}
              displayResults={decision.displayResults}
              voting={decision.voting}
              totalVotes={decision.totalVotes}
              diamonds={decision.diamonds}
              decision={decision}
              results={decision.multipleChoiceResults}
              multipleChoiceOptions={decision.multipleChoiceOptions}
              onToggle={(e) => handleToggle(e, decision.id)}
              onChangeVotingStatus={(e) =>
                handleChangeVotingStatus(e, decision.id)
              }
              multipleChoiceVoteMade={decision.multipleChoiceVoteMade}
              isDraft={decision.isDraft}
              agendaId={decision.agenda._id}
              handleProxyVote={handleProxyVote}
              isPastDecision
              readOnly={readOnly}
              isHideInfo={isHideInfo}
            />
          )}
        </div>
      );
    });
  };
  const renderAddModal = useCallback(() => {
    return (
      <>
        {account && showAddNewModal && (
          <AddNewModal
            key={id}
            isOpen={showAddNewModal}
            onClose={() => {
              setShowAddNewModal(false);
              // setIsModalOpen(false);
              refetchDashboard();
            }}
            meetingDates={currentOptionList}
            onSave={handleSaveNew}
            onSaveDraft={handleSaveNewDraft}
            currentUser={{ name: account.name, username: account.username }}
            itemOwners={itemOwners}
            isDecisionDefault={true}
            defaultDate={currentMeetingDate}
          />
        )}
        {account && editingItem && showEditModal && (
          <AddNewModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              // setIsModalOpen(false);
              refetchDashboard();
            }}
            meetingDates={currentOptionList}
            onSave={handleSaveEdit}
            onSaveDraft={handleSaveNewDraft}
            currentUser={{ name: account.name, username: account.username }}
            itemInfo={editingItem}
            itemOwners={itemOwners}
            users={currentMeeting?.users}
          />
        )}
      </>
    );
  }, [showEditModal, showAddNewModal, editingItem]);

  // Handle search past decision
  const [filter, setFilter] = useState("");
  const onInput = (e) => {
    setFilter(e.target.value.toLowerCase());
  };

  const id = nanoid();

  const renderPastDecisionWithoutSearchFilter = () => {
    return (
      <>
        {!isPastDecisionOpen && (
          <div className={styles.yearSelect}>
            <Select
              placeholder={year}
              options={pastOptionList}
              onChange={(e) => {
                setPastSelectedOption(e);
                handlePastChange(e);
              }}
              selectedOption={pastSelectedOption}
              name="year"
            />
          </div>
        )}
        {!isPastDecisionOpen &&
          sortedPastDecisions
            .filter((decisionList) => decisionList.year === pastSelectedOption)
            .map((decision, index) => (
              <div className={styles.collapsible}>
                <ProjectsPageCollapsible
                  title={decision.monthYear}
                  onClick={() => {
                    setIsPastDecisionOpen(true);
                    setCurrentSelectMonthYear(decision.monthYear);
                  }}
                />
              </div>
            ))}
        {isPastDecisionOpen && (
          <div
            className={styles.headerPastDecision}
            onClick={() => {
              setIsPastDecisionOpen(false);
            }}
          >
            <ChevronLeft size={18} />
            PAST DECISIONS
          </div>
        )}
        {isPastDecisionOpen && handleRenderPastDecision()}
      </>
    );
  };
  const renderPastDecisionOnFilter = () => {
    const agendaList = pastDecisions
      .map((meeting) => {
        return meeting.agenda
          .filter(
            (agendaItem) =>
              // Condition to filtering
              agendaItem.title.toLowerCase().includes(filter) ||
              agendaItem.description.toLowerCase().includes(filter)
          )
          .map((a) => {
            return {
              ...a,
              dates: meeting.dates,
            };
          });
      })
      .flat();
    if (agendaList.length === 0) {
      return (
        <div className={styles.error}>
          No Decisions Matching Search Available
        </div>
      );
    }
    return handleRenderPastDecision({ decisionItems: agendaList });
  };
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Header
          title={"Decisions"}
          icon={decisionsIcon}
          onRefresh={handleRefresh}
          isLoading={isRefreshing}
          children={
            <div className={styles.tabBar}>
              <TabBar
                titleList={[
                  { title: "Current Decisions", notification: 0 },
                  { title: "Past Decisions", notification: 0 },
                ]}
                activeTitle={activeTitle}
                onChange={(title) => setActiveTitle(title)}
              />
            </div>
          }
          isLock={true}
          isHide={isHideInfo}
          setToggleInfo={setToggleInfo}
        />
      </div>
      {statusCurrentDecision === "loading" ? (
        <div className={styles.noDecisions}>
          <Loader />
        </div>
      ) : (
        <div className={styles.content}>
          <ReactModal
            isOpen={deleteModelOpen}
            className={styles.deleteModal}
            overlayClassName={styles.overlay}
          >
            <div className={styles.deleteModalHeader}>
              <DeleteIcon />
              <p>
                Are you sure you want to delete this agenda item and any items
                associated with it?
              </p>
            </div>
            <div className={styles.deleteModalButtons}>
              <Button
                className={styles.cancelButton}
                children="Cancel"
                onClick={() => {
                  setDeleteModelOpen(false);
                  refetchDashboard();
                }}
              />
              <Button
                className={styles.deleteButton}
                children="Delete"
                onClick={() => {
                  handleDeleteAgenda(deleteAgendaId);
                  setDeleteModelOpen(false);
                  refetchDashboard();
                }}
                purpose="delete"
              />
            </div>
          </ReactModal>
          <div>
            {renderAddModal()}

            {showMoveModal && (
              <MoveModal
                isOpen={showMoveModal}
                onClose={() => {
                  setShowMoveModal(false);
                  // setIsModalOpen(false);
                  refetchDashboard();
                }}
                onMove={handleMove}
                onDuplicate={handleDuplicate}
              />
            )}
          </div>
          {activeTitle === "Current Decisions" &&
          currentDecisionsData &&
          currentMeetingDate ? (
            <>
              <div className={styles.banner}>
                <div className={styles.currentYearSelect}>
                  {currentMeetingDate !== null && meetings.length > 0 ? (
                    <ObjectSelect
                      options={currentOptionList}
                      onChange={(e) => handleCurrentChange(e)}
                      placeholder={currentMeetingDate.option}
                      selectedOption={currentMeetingDate}
                    />
                  ) : null}
                </div>
                <div
                  style={{ visibility: !readOnly ? "visible" : "hidden" }}
                  className={styles.addItem}
                >
                  <AddItemButton
                    title="ADD VOTING ITEM"
                    onClick={() => {
                      setShowAddNewModal(true);
                    }}
                  />
                </div>
              </div>
              {currentMeetingData && currentMeetingData.length > 0 ? (
                <>
                  <div className={styles.colName}>
                    <p>TOPIC</p>
                    <p>VOTING</p>
                    {!isHideInfo && <p>VOTING RESULTS</p>}
                    <p>DOCUMENTS</p>
                    <p>COMPLETE</p>
                  </div>
                  {currentMeetingData.map((decision, index) => (
                    <div
                      className={styles.projectItems}
                      key={`${decision._id}-${index}`}
                    >
                      {!decision.isMultipleChoice ? (
                        <DecisionStatsItem
                          readOnly={readOnly}
                          assignee={decision.assignee}
                          assignedType={decision.assignedType}
                          title={decision.title}
                          description={decision.description}
                          isCompleted={decision.isComplete}
                          votingStatus={decision.votingStatus}
                          pdfName={decision.pdfName}
                          document={decision.document}
                          onClickPdf={() =>
                            handleDownloadPdf(
                              decision.document &&
                                decision.document.files &&
                                decision.document.files.length > 0
                                ? decision.document.files
                                : null
                            )
                          }
                          onEdit={() => handleEdit(decision.agenda)}
                          onDelete={() => {
                            setDeleteAgendaId(decision.agenda._id);
                            setDeleteModelOpen(true);
                          }}
                          onMove={() => {
                            setMovingItem(decision.agenda);
                            setShowMoveModal(true);
                            // setIsModalOpen(true);
                          }}
                          onPublish={() => handlePublishAgenda(decision.agenda)}
                          displayResults={decision.displayResults}
                          voting={decision.voting}
                          totalVotes={decision.totalVotes}
                          diamonds={decision.diamonds}
                          isBlockA={decision.isBlockA}
                          isBlockB={decision.isBlockB}
                          yesVotes={decision.yesVotes}
                          noVotes={decision.noVotes}
                          decision={decision}
                          onToggle={(e) => handleToggle(e, decision.id)}
                          onChangeVotingStatus={(e) =>
                            handleChangeVotingStatus(e, decision.id)
                          }
                          handleProxyVote={handleProxyVote}
                          isDraft={decision.isDraft}
                          agendaId={decision.agenda._id}
                          isHideInfo={isHideInfo}
                        />
                      ) : (
                        <DecisionStatsMultipleChoiceItem
                          assignee={decision.assignee}
                          assignedType={decision.assignedType}
                          title={decision.title}
                          description={decision.description}
                          isCompleted={decision.isComplete}
                          votingStatus={decision.votingStatus}
                          pdfName={decision.pdfName}
                          document={decision.document}
                          onClickPdf={() => () =>
                            handleDownloadPdf(
                              decision.document &&
                                decision.document.files &&
                                decision.document.files.length > 0
                                ? decision.document.files
                                : null
                            )}
                          onEdit={() => handleEdit(decision.agenda)}
                          onDelete={() => {
                            setDeleteAgendaId(decision.agenda._id);
                            setDeleteModelOpen(true);
                          }}
                          onMove={() => {
                            setMovingItem(decision.agenda);
                            setShowMoveModal(true);
                            // setIsModalOpen(true);
                          }}
                          onPublish={() => handlePublishAgenda(decision.agenda)}
                          displayResults={decision.displayResults}
                          voting={decision.voting}
                          totalVotes={decision.totalVotes}
                          diamonds={decision.diamonds}
                          decision={decision}
                          results={decision.multipleChoiceResults}
                          multipleChoiceOptions={decision.multipleChoiceOptions}
                          onToggle={(e) => handleToggle(e, decision.id)}
                          onChangeVotingStatus={(e) =>
                            handleChangeVotingStatus(e, decision.id)
                          }
                          multipleChoiceVoteMade={
                            decision.multipleChoiceVoteMade
                          }
                          isDraft={decision.isDraft}
                          agendaId={decision.agenda._id}
                          handleProxyVote={handleProxyVote}
                          readOnly={readOnly}
                          isHideInfo={isHideInfo}
                        />
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <div className={styles.loading}>No Current Decisions</div>
              )}
            </>
          ) : activeTitle === "Past Decisions" &&
            sortedPastDecisions !== null ? (
            <>
              <div className={styles.searchBar}>
                <div className={styles.icon}>
                  <SearchIcon />
                </div>
                <Input
                  placeholder="Search Past Decisions"
                  name="search"
                  className={styles.searchInput}
                  onChange={(key) => onInput(key)}
                />
              </div>
              {filter.length > 0
                ? renderPastDecisionOnFilter()
                : renderPastDecisionWithoutSearchFilter()}
            </>
          ) : (
            <div className={styles.error}>No Decisions Available</div>
          )}
        </div>
      )}
    </div>
  );
}

const handlePastDecisionList = () => {};

// const mockData = [
//   {
//     assignee: "Greg Duncan",
//     assignedType: "WWG",
//     title: "Test Decision",
//     description: "This is a description",
//     isCompleted: false,
//     votingStatus: "Upcoming",
//     pdfName: "test-pdf",
//     displayResults: true,
//     voting: "On",
//     totalVotes: 10,
//     yesVotes: 5,
//     noVotes: 5,
//     diamonds: [
//       {
//         id: 1,
//         firstName: "Brad",
//         lastName: "Duncan",
//         photo: "SamplePhoto",
//         checkedIn: true,
//         votingStatus: "Pending",
//         isProxyVote: false,
//         role: "EC",
//       },
//       {
//         id: 1,
//         firstName: "Brad",
//         lastName: "Duncan",
//         photo: "SamplePhoto",
//         checkedIn: true,
//         votingStatus: "Pending",
//         isProxyVote: false,
//         role: "EC",
//       },
//       {
//         id: 1,
//         firstName: "Brad",
//         lastName: "Duncan",
//         photo: "SamplePhoto",
//         checkedIn: true,
//         votingStatus: "Pending",
//         isProxyVote: false,
//         role: "EC",
//       },
//     ],
//   },
//   {
//     assignee: "Greg Duncan",
//     assignedType: "WWG",
//     title: "Test Decision",
//     description: "This is a description",
//     isCompleted: false,
//     votingStatus: "Upcoming",
//     pdfName: "test-pdf",
//     displayResults: true,
//     voting: "On",
//     totalVotes: 10,
//     yesVotes: 5,
//     noVotes: 5,
//     diamonds: [
//       {
//         id: 1,
//         firstName: "Brad",
//         lastName: "Duncan",
//         photo: "SamplePhoto",
//         checkedIn: true,
//         votingStatus: "Pending",
//         isProxyVote: false,
//         role: "EC",
//       },
//       {
//         id: 1,
//         firstName: "Brad",
//         lastName: "Duncan",
//         photo: "SamplePhoto",
//         checkedIn: true,
//         votingStatus: "Pending",
//         isProxyVote: false,
//         role: "EC",
//       },
//       {
//         id: 1,
//         firstName: "Brad",
//         lastName: "Duncan",
//         photo: "SamplePhoto",
//         checkedIn: true,
//         votingStatus: "Pending",
//         isProxyVote: false,
//         role: "EC",
//       },
//     ],
//   },
//   {
//     assignee: "Greg Duncan",
//     assignedType: "WWG",
//     title: "Test Decision",
//     description: "This is a description",
//     isCompleted: false,
//     votingStatus: "Upcoming",
//     pdfName: "test-pdf",
//     displayResults: true,
//     voting: "On",
//     totalVotes: 10,
//     yesVotes: 5,
//     noVotes: 5,
//     diamonds: [
//       {
//         id: 1,
//         firstName: "Brad",
//         lastName: "Duncan",
//         photo: "SamplePhoto",
//         checkedIn: true,
//         votingStatus: "Pending",
//         isProxyVote: false,
//         role: "EC",
//       },
//       {
//         id: 1,
//         firstName: "Brad",
//         lastName: "Duncan",
//         photo: "SamplePhoto",
//         checkedIn: true,
//         votingStatus: "Pending",
//         isProxyVote: false,
//         role: "EC",
//       },
//       {
//         id: 1,
//         firstName: "Brad",
//         lastName: "Duncan",
//         photo: "SamplePhoto",
//         checkedIn: true,
//         votingStatus: "Pending",
//         isProxyVote: false,
//         role: "EC",
//       },
//     ],
//   },
//   {
//     assignee: "Greg Duncan",
//     assignedType: "WWG",
//     title: "Test Decision",
//     description: "This is a description",
//     isCompleted: false,
//     votingStatus: "Upcoming",
//     pdfName: "test-pdf",
//     displayResults: true,
//     voting: "On",
//     totalVotes: 10,
//     yesVotes: 5,
//     noVotes: 5,
//     diamonds: [
//       {
//         id: 1,
//         firstName: "Brad",
//         lastName: "Duncan",
//         photo: "SamplePhoto",
//         checkedIn: true,
//         votingStatus: "Pending",
//         isProxyVote: false,
//         role: "EC",
//       },
//       {
//         id: 1,
//         firstName: "Brad",
//         lastName: "Duncan",
//         photo: "SamplePhoto",
//         checkedIn: true,
//         votingStatus: "Pending",
//         isProxyVote: false,
//         role: "EC",
//       },
//       {
//         id: 1,
//         firstName: "Brad",
//         lastName: "Duncan",
//         photo: "SamplePhoto",
//         checkedIn: true,
//         votingStatus: "Pending",
//         isProxyVote: false,
//         role: "EC",
//       },
//     ],
//   },
// ];
